<script setup>
import { Disclosure, DisclosureButton, DisclosurePanel, Popover, PopoverButton, PopoverPanel, Menu, MenuButton, MenuItem, MenuItems, DialogPanel, Dialog, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { CreditCardIcon, MinusSmallIcon, PlusSmallIcon, QuestionMarkCircleIcon, UserIcon, EnvelopeIcon, PhoneIcon, MagnifyingGlassIcon, CheckBadgeIcon, ExclamationTriangleIcon, BuildingOfficeIcon, UserGroupIcon, CursorArrowRaysIcon, MapPinIcon, GlobeAltIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { ArrowLongRightIcon, CheckIcon, ChevronDownIcon } from '@heroicons/vue/20/solid'
import { onMounted, ref } from 'vue';

const props = defineProps(['user', 'profile', 'role', 'subscription']);

const user = ref(JSON.parse(props.user));
const profile = ref(JSON.parse(props.profile));
const role = ref(JSON.parse(props.role));

const sidebarOpen = ref(false);

const companyMenu = [
    { name: 'Angebote für Unternehmen', description: 'Erstelle ein Firmenprofil und wähle ein passendes Angebot aus.', href: '/paket', icon: CreditCardIcon },
    { name: 'Interessenten suchen', description: 'Finde interessante Mitarbeiter und kontaktiere sie direkt.', href: '/profile/', icon: UserGroupIcon },
]

const applicantMenu = [
    { name: 'Stellenangebote', description: 'Durchsuche alle Stellenangebote und finde deinen nächsten Job.', href: '/stellenangebote', icon: MagnifyingGlassIcon },
    { name: 'Unternehmen suchen', description: 'Du suchst bestimmte Arbeitgeber? Hier kannst du suchen und stöbern.', href: '/unternehmen/profile', icon: BuildingOfficeIcon },
    { name: 'Profil anlegen', description: 'Erstelle einfach ein kostenloses Profil und lasse dich von Firmen finden.', href: '/registrieren', icon: UserIcon },
]

const regionMenu = [
    { name: 'Unser Netzwerk', description: 'Mit diesen Partnern ist Dein Jobmagnet eng verbunden.', href: '/unser-netzwerk', icon: GlobeAltIcon },
]

const companyMenuActions = [
    { name: 'Hilfe & Kontakt', href: '/kontakt', icon: EnvelopeIcon }
]

const subscriptions = {
    'premium-plus': {
        'description': 'Premium-Plus Paket',
        'icon': CheckBadgeIcon,
        'class': 'tw-w-5 tw-h-5 tw-text-primary-500'
    },
    'premium': {
        'description': 'Premium Paket',
        'icon': CheckBadgeIcon,
        'class': 'tw-w-5 tw-h-5 tw-text-primary-500'
    },
    'trial': {
        'description': 'Kostenloser Testzeitraum',
        'icon': CheckBadgeIcon,
        'class': 'tw-w-5 tw-h-5 tw-text-primary-500'
    },
    'none': {
        'description': 'Basis Paket',
        'icon': ExclamationTriangleIcon,
        'class': 'tw-w-5 tw-h-5 tw-text-gray-500'
    },
}

onMounted(() => {
    if (role.value.name == 'company') {
        applicantMenu.pop();
        if (profile.value.id) {
            companyMenu.shift();
        }
    } else if (role.value.name == 'student') {
        if (profile.value.id) {
            applicantMenu.pop();
        }
        companyMenu.shift();
    }
})
</script>

<template>
    <header class="tw-bg-white tw-w-full">

        <TransitionRoot as="template" :show="sidebarOpen">
            <Dialog as="div" class="tw-relative tw-z-[9999999] lg:tw-hidden" @close="sidebarOpen = false">
                <TransitionChild as="template" enter="tw-transition-opacity tw-ease-linear tw-duration-300"
                    enter-from="tw-opacity-0" enter-to="tw-opacity-100"
                    leave="tw-transition-opacity tw-ease-linear tw-duration-300" leave-from="tw-opacity-100"
                    leave-to="tw-opacity-0">
                    <div class="tw-fixed tw-inset-0 tw-bg-gray-900/80" />
                </TransitionChild>

                <div class="tw-fixed tw-inset-0 tw-flex">
                    <TransitionChild as="template" enter="tw-transition tw-ease-in-out tw-duration-300 tw-transform"
                        enter-from="-tw-translate-x-full" enter-to="tw-translate-x-0"
                        leave="tw-transition tw-ease-in-out tw-duration-300 tw-transform" leave-from="tw-translate-x-0"
                        leave-to="-tw-translate-x-full">
                        <DialogPanel class="tw-relative tw-mr-16 tw-flex tw-w-full tw-max-w-xs tw-flex-1">
                            <TransitionChild as="template" enter="tw-ease-in-out tw-duration-300"
                                enter-from="tw-opacity-0" enter-to="tw-opacity-100"
                                leave="tw-ease-in-out tw-duration-300" leave-from="tw-opacity-100"
                                leave-to="tw-opacity-0">
                                <div
                                    class="tw-absolute tw-left-full tw-top-0 tw-flex tw-w-16 tw-justify-center tw-pt-5">
                                    <button type="button" class="-tw-m-2.5 tw-p-2.5 tw-bg-transparent"
                                        @click="sidebarOpen = false">
                                        <span class="tw-sr-only">Close sidebar</span>
                                        <XMarkIcon class="tw-h-6 tw-w-6 tw-text-white" aria-hidden="true" />
                                    </button>
                                </div>
                            </TransitionChild>
                            <div
                                class="tw-flex tw-grow tw-flex-col tw-gap-y-5 tw-overflow-y-auto tw-bg-white tw-px-6 tw-pb-4">
                                <div class="tw-flex tw-h-16 shrink-0 tw-items-center tw-mt-6">
                                    <a href="/">
                                        <img class="tw-h-11 tw-w-auto" src="/images/dein-job-magnet.svg"
                                            alt="Dein-Job-Magnet Logo" />
                                    </a>
                                </div>
                                <nav class="tw-flex tw-flex-1 tw-flex-col">
                                    <ul role="list"
                                        class="tw-flex tw-flex-1 tw-flex-col tw-gap-y-8 tw-list-none tw-pl-0">
                                        <li>
                                            <div class="tw-text-xs tw-font-semibold tw-leading-6 tw-mb-2">Jobs
                                                finden</div>
                                            <ul role="list" class="-tw-mx-2 tw-space-y-1 tw-list-none tw-pl-0">
                                                <li v-for="item in applicantMenu" :key="item.name">
                                                    <a :href="item.href"
                                                        :class="[item.current ? 'tw-bg-primary-700 tw-text-white' : '!tw-no-underline !tw-text-gray-700 hover:tw-bg-gray-200', 'tw-group tw-flex tw-gap-x-3 tw-rounded-md tw-p-2 tw-text-sm tw-leading-6 tw-font-semibold']">
                                                        <component :is="item.icon"
                                                            :class="[item.current ? 'tw-text-white' : 'tw-text-gray-700', 'tw-h-6 tw-w-6 tw-shrink-0']"
                                                            aria-hidden="true" />
                                                        {{ item.name }}
                                                    </a>
                                                </li>
                                            </ul>
                                        </li>

                                        <li>
                                            <div class="tw-text-xs tw-font-semibold tw-leading-6 tw-mb-2">Für
                                                Unternehmen</div>
                                            <ul role="list" class="-tw-mx-2 tw-space-y-1 tw-list-none tw-pl-0">
                                                <li v-for="item in companyMenu" :key="item.name">
                                                    <a :href="item.href"
                                                        :class="[item.current ? 'tw-bg-primary-700 tw-text-white' : '!tw-no-underline !tw-text-gray-700 hover:tw-bg-gray-200', 'tw-group tw-flex tw-gap-x-3 tw-rounded-md tw-p-2 tw-text-sm tw-leading-6 tw-font-semibold']">
                                                        <component :is="item.icon"
                                                            :class="[item.current ? 'tw-text-white' : 'tw-text-gray-700', 'tw-h-6 tw-w-6 tw-shrink-0']"
                                                            aria-hidden="true" />
                                                        {{ item.name }}
                                                    </a>
                                                </li>
                                            </ul>
                                        </li>

                                    </ul>
                                </nav>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>

        <div
            class="tw-fixed tw-z-[999999] tw-w-full tw-top-0 tw-bg-white/80 tw-backdrop-blur-md tw-border-secondary-200/30">

            <div class="tw-hidden sm:tw-block tw-w-full tw-bg-secondary-700">
                <div
                    class="tw-mx-auto tw-flex tw-justify-center tw-gap-x-16 tw-text-white tw-text-xs tw-font-bold tw-py-2.5">
                    <span class="tw-flex tw-items-center">
                        <CheckIcon class="tw-h-4 tw-w-auto tw-mr-1 tw-text-primary-500" />Kostenlos registrieren
                    </span>
                    <span class="tw-flex tw-items-center">
                        <CheckIcon class="tw-h-4 tw-w-auto tw-mr-1 tw-text-primary-500" />Intelligente Jobsuche
                    </span>
                    <span class="tw-flex tw-items-center">
                        <CheckIcon class="tw-h-4 tw-w-auto tw-mr-1 tw-text-primary-500" />Online-Messenger
                    </span>
                </div>
            </div>

            <nav class="tw-mx-auto tw-flex container tw-items-center tw-justify-between max-lg:tw-gap-x-2 tw-gap-x-6 tw-py-4"
                aria-label="Global">
                <div class="tw-flex">
                    <a href="/" class="-tw-m-1.5 tw-p-1.5">
                        <span class="tw-sr-only">Your Company</span>
                        <img class="tw-h-10 xl:tw-h-14 tw-w-auto" src="/images/dein-job-magnet.svg" alt="">
                    </a>
                </div>
                <div class="tw-hidden lg:tw-flex lg:tw-gap-x-20 lg:tw-space-between">
                    <Popover class="tw-relative">
                        <PopoverButton
                            class="tw-bg-transparent tw-inline-flex tw-items-center tw-gap-x-1 tw-text-sm tw-font-semibold tw-leading-6 tw-text-gray-900">
                            <span>Jobs finden</span>
                            <ChevronDownIcon class="tw-h-5 tw-w-5" aria-hidden="true" />
                        </PopoverButton>

                        <transition enter-active-class="tw-transition tw-ease-out tw-duration-200"
                            enter-from-class="tw-opacity-0 tw-translate-y-1"
                            enter-to-class="tw-opacity-100 tw-translate-y-0"
                            leave-active-class="tw-transition tw-ease-in tw-duration-150"
                            leave-from-class="tw-opacity-100 tw-translate-y-0"
                            leave-to-class="tw-opacity-0 tw-translate-y-1">
                            <PopoverPanel
                                class="tw-absolute tw-left-1/2 tw-z-10 tw-mt-5 tw-flex tw-w-screen tw-max-w-max -tw-translate-x-1/2 tw-px-4">
                                <div
                                    class="tw-w-screen tw-max-w-md tw-flex-auto tw-overflow-hidden tw-rounded-3xl tw-bg-white tw-text-sm tw-leading-6 tw-shadow-lg tw-ring-1 tw-ring-gray-900/5">
                                    <div class="tw-p-4">
                                        <div v-for="item in applicantMenu" :key="item.name"
                                            class="tw-group tw-relative tw-flex tw-gap-x-6 tw-rounded-lg tw-p-4 hover:tw-bg-gray-50">
                                            <div
                                                class="tw-mt-1 tw-flex tw-h-11 tw-w-11 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-gray-50 group-hover:tw-bg-white">
                                                <component :is="item.icon"
                                                    class="tw-h-6 tw-w-6 tw-text-gray-600 group-hover:tw-text-primary-500"
                                                    aria-hidden="true" />
                                            </div>
                                            <div>
                                                <a :href="item.href"
                                                    class="!tw-no-underline tw-font-semibold !tw-text-gray-900">
                                                    {{ item.name }}
                                                    <span class="tw-absolute tw-inset-0" />
                                                </a>
                                                <p class="tw-mt-1 tw-text-gray-600">{{ item.description }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </PopoverPanel>
                        </transition>
                    </Popover>

                    <Popover class="tw-relative">
                        <PopoverButton
                            class="tw-bg-transparent tw-inline-flex tw-items-center tw-gap-x-1 tw-text-sm tw-font-semibold tw-leading-6 tw-text-gray-900">
                            <span>Für Unternehmen</span>
                            <ChevronDownIcon class="tw-h-5 tw-w-5" aria-hidden="true" />
                        </PopoverButton>

                        <transition enter-active-class="tw-transition tw-ease-out tw-duration-200"
                            enter-from-class="tw-opacity-0 tw-translate-y-1"
                            enter-to-class="tw-opacity-100 tw-translate-y-0"
                            leave-active-class="tw-transition tw-ease-in tw-duration-150"
                            leave-from-class="tw-opacity-100 tw-translate-y-0"
                            leave-to-class="tw-opacity-0 tw-translate-y-1">
                            <PopoverPanel
                                class="tw-absolute tw-left-1/2 tw-z-10 tw-mt-5 tw-flex tw-w-screen tw-max-w-max -tw-translate-x-1/2 tw-px-4">
                                <div
                                    class="tw-w-screen tw-max-w-md tw-flex-auto tw-overflow-hidden tw-rounded-3xl tw-bg-white tw-text-sm tw-leading-6 tw-shadow-lg tw-ring-1 tw-ring-gray-900/5">
                                    <div class="tw-p-4">
                                        <div v-for="item in companyMenu" :key="item.name"
                                            class="tw-group tw-relative tw-flex tw-gap-x-6 tw-rounded-lg tw-p-4 hover:tw-bg-gray-50">
                                            <div
                                                class="tw-mt-1 tw-flex tw-h-11 tw-w-11 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-gray-50 group-hover:tw-bg-white">
                                                <component :is="item.icon"
                                                    class="tw-h-6 tw-w-6 tw-text-gray-600 group-hover:tw-text-primary-500"
                                                    aria-hidden="true" />
                                            </div>
                                            <div>
                                                <a :href="item.href"
                                                    class="!tw-no-underline tw-font-semibold !tw-text-gray-900">
                                                    {{ item.name }}
                                                    <span class="tw-absolute tw-inset-0" />
                                                </a>
                                                <p class="tw-mt-1 tw-text-gray-600">{{ item.description }}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="tw-grid tw-grid-cols-1 tw-divide-x divide-gray-900/5 tw-bg-gray-50">
                                        <a v-for="item in companyMenuActions" :key="item.name" :href="item.href"
                                            class="!tw-no-underline tw-flex tw-items-center tw-justify-center tw-gap-x-2.5 tw-p-3 tw-font-semibold !tw-text-gray-900 hover:tw-bg-gray-100">
                                            <component :is="item.icon"
                                                class="tw-h-5 tw-w-5 tw-flex-none tw-text-gray-600"
                                                aria-hidden="true" />
                                            {{ item.name }}
                                        </a>
                                    </div>
                                </div>
                            </PopoverPanel>
                        </transition>
                    </Popover>

                </div>

                <Menu v-if="user.id" as="div" class="tw-relative tw-justify-end tw-flex">

                    <div class="tw-flex tw-items-center">

                        <messenger-button>
                            <message-counter :user-id="user.id"></message-counter>
                            <div
                                class="tw-flex tw-items-center tw-justify-center tw-bg-primary-500 tw-rounded-md tw-cursor-pointer tw-transition hover:tw-bg-primary-400 tw-p-2.5">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                    stroke-width="1.5" stroke="currentColor" class="tw-w-6 tw-h-6 tw-text-white">
                                    <path stroke-linecap="round" stroke-linejoin="round"
                                        d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
                                </svg>
                            </div>
                        </messenger-button>

                    </div>

                    <div class="tw-flex tw-items-center">
                        <MenuButton
                            class="tw-bg-transparent tw-inline-flex tw-w-full tw-justify-center tw-text-sm tw-font-semibold tw-text-gray-900 tw-px-2">

                            <div class="tw-flex tw-items-center">
                                <div v-if="role.name == 'student'">
                                    <img class="tw-inline-block tw-h-9 tw-w-9 tw-rounded-full tw-mx-2 max-lg:tw-hidden max-[1200px]:tw-mr-10"
                                        :src="'/images/avatar/cropped-' + profile.image" alt="" />
                                </div>
                                <div class="tw-text-left">

                                    <p
                                        class="lg:tw-block tw-hidden tw-text-sm tw-font-medium tw-text-gray-700 group-hover:tw-text-gray-900 tw-mb-0">
                                        {{ user.name }} {{ profile.last_name }}
                                    </p>

                                    <div class="tw-block lg:tw-hidden">
                                        <button type="button"
                                            class="tw-flex tw-items-center tw-justify-center tw-rounded-md tw-p-2.5 tw-text-gray-700">
                                            <UserIcon class="tw-h-6 tw-w-6"></UserIcon>
                                        </button>
                                    </div>

                                    <p v-if="role.name == 'company'"
                                        class="tw-text-xs tw-font-medium tw-text-gray-500 group-hover:tw-text-gray-700 tw-mb-0 xl:tw-block tw-hidden">

                                        <span
                                            class="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-x-1 tw-rounded-full tw-w-fit">
                                            <component :is="subscriptions[props.subscription].icon"
                                                :class="subscriptions[props.subscription].class" aria-hidden="true" />
                                            {{ subscriptions[props.subscription].description }}
                                        </span>
                                    </p>

                                    <p v-else
                                        class="max-lg:tw-hidden tw-text-xs tw-font-medium tw-text-gray-500 group-hover:tw-text-gray-700 tw-mb-0">
                                        <span
                                            class="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-x-1 tw-rounded-full tw-w-fit">
                                            Einstellungen
                                        </span>
                                    </p>

                                </div>
                            </div>
                            <ChevronDownIcon class="lg:tw-block tw-hidden tw-h-5 tw-w-5" aria-hidden="true" />
                        </MenuButton>
                    </div>

                    <transition enter-active-class="tw-transition tw-ease-out tw-duration-100"
                        enter-from-class="tw-transform tw-opacity-0 tw-scale-95"
                        enter-to-class="tw-transform tw-opacity-100 tw-scale-100"
                        leave-active-class="tw-transition tw-ease-in tw-duration-75"
                        leave-from-class="tw-transform tw-opacity-100 tw-scale-100"
                        leave-to-class="tw-transform tw-opacity-0 tw-scale-95">
                        <MenuItems
                            class="tw-absolute tw-right-0 tw-z-10 tw-mt-14 tw-w-56 tw-origin-top-right tw-divide-y tw-divide-gray-100 tw-rounded-md tw-bg-white tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none">
                            <div class="tw-px-4 tw-py-3">
                                <p class="tw-text-sm tw-mb-0">Angemeldet als</p>
                                <p class="tw-truncate tw-mb-0 tw-text-sm tw-font-medium tw-text-gray-900">{{ user.email
                                    }}
                                </p>
                            </div>
                            <div class="tw-py-1">

                                <MenuItem v-if="role.name == 'admin' || role.name == 'partner'" v-slot="{ active }">
                                <a href="/administration/unternehmen"
                                    :class="[active ? 'tw-bg-gray-100 !tw-text-gray-900 !tw-no-underline' : 'tw-text-gray-700', 'tw-block tw-px-4 tw-py-2 tw-text-sm']">Administration</a>
                                </MenuItem>

                                <template v-if="role.name == 'company'">
                                    <template v-if="subscription == 'premium-plus' || subscription == 'premium'">
                                        <MenuItem v-slot="{ active }">
                                        <a href="/paket/buchen"
                                            :class="[active ? 'tw-bg-gray-100 !tw-text-gray-900 !tw-no-underline' : 'tw-text-gray-700', 'tw-block tw-px-4 tw-py-2 tw-text-sm']">Paket
                                            verwalten</a>
                                        </MenuItem>
                                    </template>
                                    <template v-else>
                                        <MenuItem v-slot="{ active }">
                                        <a href="/paket/buchen"
                                            :class="[active ? 'tw-bg-gray-100 !tw-text-gray-900 !tw-no-underline' : 'tw-text-gray-700', 'tw-block tw-px-4 tw-py-2 tw-text-sm']">Paket
                                            buchen</a>
                                        </MenuItem>
                                    </template>
                                </template>

                                <template v-if="profile.id">
                                    <template v-if="role.name == 'student'">
                                        <MenuItem v-slot="{ active }">
                                        <a :href="'/profile/' + profile.id"
                                            :class="[active ? 'tw-bg-gray-100 !tw-text-gray-900 !tw-no-underline' : 'tw-text-gray-700', 'tw-block tw-px-4 tw-py-2 tw-text-sm']">Mein
                                            Profil</a>
                                        </MenuItem>
                                        <MenuItem v-slot="{ active }">
                                        <a :href="'/profile/' + profile.id + '/bearbeiten'"
                                            :class="[active ? 'tw-bg-gray-100 !tw-text-gray-900 !tw-no-underline' : 'tw-text-gray-700', 'tw-block tw-px-4 tw-py-2 tw-text-sm']">Profil
                                            bearbeiten</a>
                                        </MenuItem>
                                    </template>
                                    <template v-else-if="role.name == 'company'">
                                        <MenuItem v-slot="{ active }">
                                        <a :href="'/unternehmen/profile/' + profile.id"
                                            :class="[active ? 'tw-bg-gray-100 !tw-text-gray-900 !tw-no-underline' : 'tw-text-gray-700', 'tw-block tw-px-4 tw-py-2 tw-text-sm']">Mein
                                            Profil</a>
                                        </MenuItem>
                                        <MenuItem v-slot="{ active }">
                                        <a :href="'/unternehmen/profile/' + profile.id + '/bearbeiten'"
                                            :class="[active ? 'tw-bg-gray-100 !tw-text-gray-900 !tw-no-underline' : 'tw-text-gray-700', 'tw-block tw-px-4 tw-py-2 tw-text-sm']">Profil
                                            bearbeiten</a>
                                        </MenuItem>
                                    </template>
                                </template>
                                <template v-else>
                                    <template v-if="role.name == 'student'">
                                        <MenuItem v-slot="{ active }">
                                        <a :href="'/profile/anlegen'"
                                            :class="[active ? 'tw-bg-gray-100 !tw-text-gray-900 !tw-no-underline' : 'tw-text-gray-700', 'tw-block tw-px-4 tw-py-2 tw-text-sm']">Profil
                                            anlegen</a>
                                        </MenuItem>
                                    </template>
                                    <template v-else-if="role.name == 'company'">
                                        <MenuItem v-slot="{ active }">
                                        <a :href="'/unternehmen/' + user.id + '/profile/anlegen'"
                                            :class="[active ? 'tw-bg-gray-100 !tw-text-gray-900 !tw-no-underline' : 'tw-text-gray-700', 'tw-block tw-px-4 tw-py-2 tw-text-sm']">Profil
                                            anlegen</a>
                                        </MenuItem>
                                    </template>
                                </template>

                                <MenuItem v-slot="{ active }">
                                <a :href="'/benutzer/' + user.id + '/bearbeiten'"
                                    :class="[active ? 'tw-bg-gray-100 !tw-text-gray-900 !tw-no-underline' : 'tw-text-gray-700', 'tw-block tw-px-4 tw-py-2 tw-text-sm']">Kontoeinstellungen</a>
                                </MenuItem>

                            </div>
                            <div class="tw-py-1">
                                <MenuItem v-slot="{ active }">
                                <a href="/abmelden"
                                    :class="[active ? 'tw-bg-gray-100 !tw-text-red-900 !tw-no-underline' : 'tw-text-red-700', 'tw-block tw-px-4 tw-py-2 tw-text-sm']">Abmelden</a>
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </transition>
                </Menu>

                <div v-else class="tw-hidden sm:tw-flex tw-items-center tw-justify-end tw-gap-x-6 tw-mr-3">
                    <a href="/anmelden"
                        class="!tw-no-underline tw-hidden xl:tw-block lg:tw-text-sm lg:tw-font-semibold lg:tw-leading-6 !tw-text-gray-900">Anmelden</a>
                    <a href="/registrieren"
                        class="!tw-no-underline tw-rounded-md tw-bg-primary-500 tw-px-3 tw-py-3 tw-text-sm tw-font-semibold !tw-text-white tw-shadow-sm hover:tw-bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:tw-outline-primary-600">Profil
                        anlegen</a>
                </div>

                <div class="tw-flex tw-items-center lg:tw-hidden">
                    <div v-if="!user.id">
                        <div class="tw-block sm:tw-hidden">
                            <a href="/anmelden"
                                class="tw-mr-5 -tw-m-2.5 tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-p-2.5 !tw-text-white tw-ml-1 -tw-mt-0.5 tw-bg-primary-500 tw-transition hover:tw-bg-primary-400">
                                <UserIcon class="tw-h-6 tw-w-6"></UserIcon>
                            </a>
                        </div>
                    </div>

                    <div class="tw-flex lg:tw-hidden">
                        <button type="button" @click="sidebarOpen = true"
                            class="-tw-ml-2.5 tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-p-2.5 tw-text-gray-700">
                            <span class="tw-sr-only">Open main menu</span>
                            <svg class="tw-h-6 tw-w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="currentColor" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                            </svg>
                        </button>
                    </div>
                </div>
            </nav>
        </div>
    </header>
</template>
